import React from 'react';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { Box, Button, Paper, Typography } from '@mui/material';
import { format } from 'date-fns';
import { ClientNameLink } from '@/components/clients/ClientNameLink';

export interface AppointmentListCardProps {
  startTime: Date;
  primaryLabel?: string;
  primaryLabelClientId?: string | number | null;
  title: string;
  modeLabel: string;
  createdByLabel?: string;
  status?: string;
  hideActions?: boolean;
  showJoin?: boolean;
  titleTextColor?: string;
  titleFontWeight?: number;
  dateBackgroundColor?: string;
  dateTextColor?: string;
  timeTextColor?: string;
  timeFontWeight?: number;
  timeMarginTop?: number;
  onOpen: () => void;
  onReschedule?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onCopyRescheduleLink?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onJoin?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onCancel?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const AppointmentListCard: React.FC<AppointmentListCardProps> = ({
  startTime,
  primaryLabel,
  primaryLabelClientId,
  title,
  modeLabel,
  createdByLabel,
  status = '',
  hideActions = false,
  showJoin = false,
  titleTextColor,
  titleFontWeight,
  dateBackgroundColor,
  dateTextColor,
  timeTextColor,
  timeFontWeight,
  timeMarginTop,
  onOpen,
  onReschedule,
  onCopyRescheduleLink,
  onJoin,
  onCancel,
}) => {
  const isPending = status.toLowerCase() === 'pending';
  const hasPrimaryLabel = Boolean(primaryLabel?.trim());
  const resolvedDateTextColor = dateTextColor || (dateBackgroundColor ? '#FFFFFF' : '#34363B');
  const resolvedMonthTextColor = dateTextColor || (dateBackgroundColor ? '#FFFFFF' : '#8C929C');

  return (
    <Paper
      elevation={0}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      sx={{
        borderRadius: '14px',
        border: '1px solid #DEE5EE',
        bgcolor: '#fff',
        px: { xs: 1.35, md: 2 },
        py: { xs: 1.15, md: 1.25 },
        minHeight: { md: 88 },
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: '64px minmax(0, 1fr) auto',
        },
        alignItems: 'center',
        columnGap: { xs: 0, md: 1.85 },
        rowGap: { xs: 0.8, md: 0 },
        cursor: 'pointer',
        outline: 'none',
        '&:focus-visible': {
          boxShadow: '0 0 0 2px #3BAEDB',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          minHeight: { md: 48 },
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          alignItems: { xs: 'center', md: 'center' },
          gap: { xs: 0.55, md: 0.15 },
          justifyContent: 'center',
          width: { xs: 'fit-content', md: '100%' },
          px: dateBackgroundColor ? { xs: 1.2, md: 0.8 } : 0,
          py: dateBackgroundColor ? { xs: 0.55, md: 0.85 } : 0,
          borderRadius: dateBackgroundColor ? '12px' : 0,
          bgcolor: dateBackgroundColor || 'transparent',
          pr: { md: 1.65 },
          '&::after': {
            content: '""',
            display: dateBackgroundColor ? 'none' : { xs: 'none', md: 'block' },
            position: 'absolute',
            top: 0,
            right: 0,
            width: '1px',
            height: 48,
            bgcolor: '#EEF2F6',
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: { xs: '1.25rem', md: '1.95rem' },
            lineHeight: 1,
            letterSpacing: 0,
            color: resolvedDateTextColor,
          }}
        >
          {format(startTime, 'dd')}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 800,
            fontSize: { xs: '0.75rem', md: '0.76rem' },
            letterSpacing: 0,
            color: resolvedMonthTextColor,
          }}
        >
          {format(startTime, 'MMM').toUpperCase()}
        </Typography>
      </Box>

      <Box sx={{ minWidth: 0, alignSelf: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.55, md: 0.9 }, flexWrap: 'wrap' }}>
          {hasPrimaryLabel && (
            <>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  lineHeight: 1.2,
                  color: '#1F2937',
                  overflowWrap: 'anywhere',
                }}
              >
                <ClientNameLink clientId={primaryLabelClientId} className="text-[#1F2937] hover:text-[#1F2937]">
                  {primaryLabel}
                </ClientNameLink>
              </Typography>

              <Box
                component="span"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  width: '1px',
                  height: { sm: 18, md: 20 },
                  bgcolor: '#D7DEE8',
                }}
              />
            </>
          )}

          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: titleFontWeight || 500,
              fontSize: { xs: '0.86rem', md: '0.9rem' },
              lineHeight: 1.25,
              color: titleTextColor || '#00BDA5',
              overflowWrap: 'anywhere',
            }}
          >
            {title}
          </Typography>

          {isPending && (
            <Box
              component="span"
              sx={{
                px: 0.75,
                py: 0.1,
                borderRadius: '999px',
                border: '1px solid #FEC84B',
                bgcolor: '#FFFAEB',
                color: '#B54708',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              Request
            </Box>
          )}
        </Box>

        <Typography
          sx={{
            mt: timeMarginTop ?? { xs: 0.55, md: 0.6 },
            fontFamily: 'Inter, sans-serif',
            fontWeight: timeFontWeight || 300,
            fontSize: { xs: '0.75rem', md: '0.8rem' },
            lineHeight: 1.2,
            color: '#8C95A6',
          }}
        >
          <Box component="span" sx={{ color: timeTextColor || '#8C95A6' }}>
            {format(startTime, 'h:mm a')}
          </Box>
          {' • '}
          {modeLabel}
          {createdByLabel && (
            <>
              {' • '}
              Created by {createdByLabel}
            </>
          )}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'flex-start', md: 'flex-end' },
          gap: 0.85,
          flexWrap: 'wrap',
          width: { xs: '100%', md: 'auto' },
        }}
      >
        {!hideActions && (
          <>
            {onCopyRescheduleLink && (
              <Button
                variant="outlined"
                onClick={onCopyRescheduleLink}
                sx={{
                  minHeight: 32,
                  px: 1.3,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: '#667085',
                  borderColor: '#D0D5DD',
                  flex: { xs: '1 1 auto', md: '0 0 auto' },
                  '&:hover': {
                    borderColor: '#BFC7D2',
                    bgcolor: '#F8FAFC',
                  },
                }}
              >
                Copy Reschedule Link
              </Button>
            )}
            
            {onReschedule && (
              <Button
                variant="outlined"
                onClick={onReschedule}
                sx={{
                  minHeight: 32,
                  minWidth: { xs: 0, md: 90 },
                  px: 1.3,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: '#667085',
                  borderColor: '#D0D5DD',
                  flex: { xs: '1 1 auto', md: '0 0 auto' },
                  '&:hover': {
                    borderColor: '#BFC7D2',
                    bgcolor: '#F8FAFC',
                  },
                }}
              >
                Reschedule
              </Button>
            )}

            {showJoin && onJoin && (
              <Button
                disableElevation
                onClick={onJoin}
                startIcon={<VideocamOutlinedIcon sx={{ fontSize: 14 }} />}
                sx={{
                  minHeight: 32,
                  minWidth: { xs: 0, md: 78 },
                  px: 1.35,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  bgcolor: '#3BAEDB',
                  color: '#fff',
                  flex: { xs: '1 1 auto', md: '0 0 auto' },
                  '&:hover': {
                    bgcolor: '#2D9DCB',
                  },
                  '& .MuiButton-startIcon': {
                    mr: 0.45,
                  },
                }}
              >
                Join
              </Button>
            )}

            {onCancel && (
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{
                  minHeight: 32,
                  minWidth: { xs: 0, md: 68 },
                  px: 1.15,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: '#F04438',
                  borderColor: '#FECDCA',
                  flex: { xs: '1 1 auto', md: '0 0 auto' },
                  '&:hover': {
                    borderColor: '#FDA29B',
                    bgcolor: '#FFF5F5',
                  },
                }}
              >
                Cancel
              </Button>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default AppointmentListCard;
